import { Component, OnInit, signal, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from "@angular/material/dialog";
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {GamePlay} from "../../model/game-play";
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { TranslocoModule } from '@ngneat/transloco';

interface DialogData {
  row: GamePlay;
  plays?: GamePlay[];
}

@Component({
    selector: 'app-dialog-kick-stats',
    templateUrl: './dialog-kick-stats.component.html',
    styleUrls: ['./dialog-kick-stats.component.scss'],
    imports: [MatDialogTitle, CdkScrollable, MatDialogContent, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatSuffix, MatDialogActions, MatButton, MatDialogClose, TranslocoModule]
})
export class DialogKickStatsComponent implements OnInit{
  private fb = inject(FormBuilder);
  private dialogRef = inject<MatDialogRef<DialogKickStatsComponent>>(MatDialogRef);
  data = inject<DialogData>(MAT_DIALOG_DATA);

  kickStatsForm!: FormGroup;
  returnYard = signal(0);
  kickYard = signal(0);
  startingYardLine = 0;
  private isRecalculating = false;

  ngOnInit() {
    // Parse yardline from string (e.g., "45", "-30")
    this.startingYardLine = this.parseYardLine(this.data.row.yardLine);
    
    // Valeurs existantes dans la row
    const existingKickYards = this.data.row.kickYards ?? 0;
    const existingReturnYards = this.data.row.returnYards ?? 0;
    
    // Calculer les yardlines depuis les valeurs existantes si disponibles
    let initialCaughtOn = '';
    const initialEndYardLine = this.findNextRowYardLine();
    
    if (existingKickYards && existingKickYards > 0) {
      // Système d'index: convertir yardline vers index, ajouter kickYards, reconvertir
      const startIndex = this.yardLineToIndex(this.startingYardLine);
      const endIndex = startIndex + existingKickYards;
      const calculatedCaughtOn = this.indexToYardLine(endIndex);
      initialCaughtOn = calculatedCaughtOn.toString();
    }
    
    this.kickStatsForm = this.fb.group({
      caughtOn: [initialCaughtOn, [Validators.required, Validators.min(-20), Validators.max(55)]],
      endYardLine: [initialEndYardLine, [Validators.required, Validators.min(-54), Validators.max(55)]],
      kickYards: [existingKickYards],
      returnYards: [existingReturnYards],
    });
    
    // Calculer les yards dès le départ pour comparer avec les valeurs existantes
    this.recalculate();
    
    // Écouter les changements de kickYards pour calculer caughtOn
    this.kickStatsForm.get('kickYards')?.valueChanges.subscribe(value => {
      if (this.isRecalculating) return;
      
      this.kickYard.set(value || 0);
      if (value != null && !isNaN(value)) {
        const startIndex = this.yardLineToIndex(this.startingYardLine);
        const endIndex = startIndex + value;
        const caughtOn = this.indexToYardLine(endIndex);
        const clampedCaughtOn = Math.max(-54, Math.min(54, caughtOn));
        this.kickStatsForm.patchValue({ caughtOn: clampedCaughtOn }, { emitEvent: false });
      }
    });
    
    // Écouter les changements de returnYards pour calculer endYardLine
    this.kickStatsForm.get('returnYards')?.valueChanges.subscribe(value => {
      if (this.isRecalculating) return;
      
      this.returnYard.set(value || 0);
      const caughtOn = Number(this.kickStatsForm.value.caughtOn);
      if (value != null && !isNaN(value) && !isNaN(caughtOn)) {
        const caughtOnIndex = this.yardLineToIndex(caughtOn);
        const endIndex = caughtOnIndex + value;
        const endYardLine = this.indexToYardLine(endIndex);
        const clampedEndYardLine = Math.max(-54, Math.min(54, endYardLine));
        this.kickStatsForm.patchValue({ endYardLine: clampedEndYardLine }, { emitEvent: false });
      }
    });
  }

  parseYardLine(yardLine: string | undefined): number {
    if (!yardLine) return 0;
    const parsed = parseInt(yardLine, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  findNextRowYardLine(): number {
    if (!this.data.plays || this.data.plays.length === 0) return 0;
    
    const currentIndex = this.data.plays.findIndex(p => p.id === this.data.row.id);
    if (currentIndex === -1 || currentIndex === this.data.plays.length - 1) return 0;
    
    const nextPlay = this.data.plays[currentIndex + 1];
    const nextYardLine = this.parseYardLine(nextPlay.yardLine);
    
    // Inverser la polarité sauf si c'est la ligne 55
    if (nextYardLine === 55) return 55;
    return -nextYardLine;
  }

  recalculate() {
    const caughtOn = Number(this.kickStatsForm.value.caughtOn);
    const endYardLine = Number(this.kickStatsForm.value.endYardLine);

    this.isRecalculating = true;

    // Calcul du kick yard (de la yardline de début à celle où le ballon a été attrapé)
    if (!isNaN(caughtOn)) {
      const startIndex = this.yardLineToIndex(this.startingYardLine);
      const caughtIndex = this.yardLineToIndex(caughtOn);
      const kickYards = Math.abs(caughtIndex - startIndex);
      this.kickYard.set(kickYards);
      this.kickStatsForm.patchValue({ kickYards }, { emitEvent: false });
    }

    // Calcul du retour (de là où le ballon a été attrapé à là où le jeu s'est terminé)
    if (!isNaN(endYardLine) && !isNaN(caughtOn)) {
      const caughtIndex = this.yardLineToIndex(caughtOn);
      const endIndex = this.yardLineToIndex(endYardLine);
      const returnYards = Math.abs(endIndex - caughtIndex);
      this.returnYard.set(returnYards);
      this.kickStatsForm.patchValue({ returnYards }, { emitEvent: false });
    }

    this.isRecalculating = false;
  }

  // Conversion yardline vers index: 
  // -1 = index 0, -45 = index 44, -54 = index 53
  // 55 = index 54
  // 1 = index 108
  yardLineToIndex(yardLine: number): number {
    if (yardLine < 0) {
      // -1 = 0, donc index = -yardLine - 1
      return -yardLine - 1;
    } else if (yardLine === 55) {
      return 54;
    } else {
      // 54 à 1 (index 55 à 108)
      return 109 - yardLine;
    }
  }

  // Conversion index vers yardline
  indexToYardLine(index: number): number {
    if (index < 54) {
      // Index 0 à 53 → yardline -1 à -54
      return -(index + 1);
    } else if (index === 54) {
      return 55;
    } else {
      // Index 55 à 108 → yardline 54 à 1
      return 109 - index;
    }
  }

  // Vérifier si les valeurs calculées diffèrent des valeurs existantes
  get hasValueChanged() {
    const existingKickYards = this.data.row.kickYards ?? 0;
    const existingReturnYards = this.data.row.returnYards ?? 0;
    
    return {
      kickYardsChanged: Math.abs(existingKickYards - this.kickYard()) > 0,
      returnYardsChanged: Math.abs(existingReturnYards - this.returnYard()) > 0
    };
  }

  computeYards(from: number, to: number): number {
    // si même côté du terrain
    if (Math.sign(from) === Math.sign(to)) {
      return Math.abs(to - from);
    }

    // si de - à +
    return Math.abs(from) + Math.abs(to);
  }

  // Calcul inverse : trouver une yardline à partir d'une yardline de départ et des yards parcourus
  computeYardLineFromYards(from: number, yards: number): number {
    if (yards === 0) return from;
    
    // Dans le football, si on part de yardline négative et qu'on botte,
    // on va vers les yardlines positives (vers la zone adverse)
    // -45 + 54 = +9
    
    // Si on part de yardline positive et qu'on botte,
    // on va vers les yardlines négatives (vers la zone adverse)
    // +45 botte vers -45 ou similaire
    
    const target = from + yards;
    
    // Limiter à la zone valide [-54, 55] pour caughtOn, [-54, 55] pour endYardLine
    if (target > 55) return 55;
    if (target < -54) return -54;
    
    return target;
  }

  updateKickStats() {
    // Récupérer les valeurs du formulaire (en cas d'édition manuelle)
    const formValue = this.kickStatsForm.value;
    
    // Mettre à jour les valeurs dans la row
    this.data.row.kickYards = formValue.kickYards || this.kickYard();
    this.data.row.returnYards = formValue.returnYards || this.returnYard();
    
    // Retourner les valeurs pour que le parent puisse les utiliser
    this.dialogRef.close({ 
      kickYards: this.data.row.kickYards,
      returnYards: this.data.row.returnYards,
      row: this.data.row
    });
  }
}
